import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  ContextMenuComponent,
  ContextMenuModule,
} from '@perfectmemory/ngx-contextmenu';
import {
  DocPopupMenuFlatEntry,
  DocPopupMenuItem,
  flattenDocPopupMenuItems,
} from '../popup-menu/doc-popup-menu.types';

@Component({
  selector: 'doc-context-menu',
  standalone: true,
  imports: [CommonModule, ContextMenuModule],
  template: `
    <context-menu #menu [menuClass]="menuClassNames" (close)="handleClose()">
      @for (entry of flatModel; track entry.id) {
        @if (entry.kind === 'separator') {
          <ng-template contextMenuItem [divider]="true"></ng-template>
        } @else if (entry.kind === 'item') {
          <ng-template
            contextMenuItem
            [disabled]="isItemDisabled(entry.item)"
            [visible]="isItemVisible(entry.item)"
            (execute)="onItemExecute(entry.item, $event)">
            <div
              class="doc-context-menu-item"
              [class.disabled]="isItemDisabled(entry.item)"
              [ngClass]="entry.item.styleClass">
              @if (entry.item.icon) {
                <i [class]="entry.item.icon + ' doc-context-menu-icon'" aria-hidden="true"></i>
              }
              @if (entry.item.escape === false) {
                <span class="doc-context-menu-text" [innerHTML]="entry.item.label"></span>
              } @else {
                <span class="doc-context-menu-text">{{ entry.item.label }}</span>
              }
            </div>
          </ng-template>
        }
      }
    </context-menu>
  `,
  styleUrl: './doc-context-menu.component.scss',
})
export class DocContextMenuComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() model: DocPopupMenuItem[] | null = [];
  /** PrimeNG-compatible alias for menuClass. */
  @Input() styleClass = '';

  @Output() onHide = new EventEmitter<void>();

  @ViewChild('menu') private menu!: ContextMenuComponent<unknown>;

  get menuClassNames(): string {
    return ['doc-context-menu-panel', this.styleClass].filter(Boolean).join(' ');
  }

  get flatModel(): DocPopupMenuFlatEntry[] {
    return flattenDocPopupMenuItems(this.model);
  }

  get visible(): boolean {
    return this.menu?.isOpen ?? false;
  }

  /**
   * Opens the menu at the pointer. Optionally pass `model` so items are available
   * immediately — parent `@Input` binding may not have flushed yet on first open.
   */
  show(event: MouseEvent, model?: DocPopupMenuItem[] | null): void {
    if (arguments.length > 1) {
      this.model = model ?? [];
    }

    const x = event.clientX;
    const y = event.clientY;

    // Build @for templates before ngx-contextmenu reads them.
    this.cdr.detectChanges();

    // Defer one macrotask so ContentChildren/templates are registered.
    setTimeout(() => {
      this.cdr.detectChanges();
      if (!this.flatModel.some((entry) => entry.kind === 'item')) {
        return;
      }
      this.menu.show({
        anchoredTo: 'position',
        x,
        y,
      });
    });
  }

  hide(): void {
    this.menu.hide();
  }

  handleClose(): void {
    this.onHide.emit();
  }

  isItemDisabled(item: DocPopupMenuItem): boolean {
    return !!item.disabled;
  }

  isItemVisible(item: DocPopupMenuItem): boolean {
    return item.visible !== false;
  }

  onItemExecute(
    item: DocPopupMenuItem,
    executeEvent: { event: MouseEvent | KeyboardEvent; value?: unknown }
  ): void {
    if (this.isItemDisabled(item)) {
      return;
    }

    item.command?.({ originalEvent: executeEvent.event, item });
    this.hide();
  }
}
